import React, { useState, useEffect, useRef } from 'react';
import { Terminal, Database, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { SimulationState } from '../../types';

interface BottomPanelProps {
  simulationState: SimulationState;
  onVariableChange?: (key: string, value: any) => void;
}

const BottomPanel: React.FC<BottomPanelProps> = ({ simulationState, onVariableChange }) => {
  // Collapsed by default
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'logs' | 'variables'>('logs');
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom of logs
  useEffect(() => {
    if (activeTab === 'logs' && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [simulationState.logs, activeTab, isOpen]);

  if (!isOpen) {
      return (
          <div className="h-9 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 flex items-center px-4 justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors z-20" onClick={() => setIsOpen(true)}>
             <div className="flex gap-4 text-xs font-medium text-slate-500">
                <span className="flex items-center gap-2"><Terminal size={14}/> Logs</span>
                <span className="flex items-center gap-2"><Database size={14}/> Variables ({Object.keys(simulationState.variables).length})</span>
             </div>
             <ChevronUp size={16} className="text-slate-400"/>
          </div>
      )
  }

  return (
    <div className="h-[250px] border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 flex flex-col shadow-inner transition-all duration-300 z-20">
       {/* Toolbar */}
       <div className="h-10 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-2 bg-slate-50 dark:bg-slate-900 select-none">
           <div className="flex gap-1 pt-1 h-full items-end">
               <button 
                  onClick={() => setActiveTab('logs')}
                  className={`px-4 py-2 text-xs font-bold flex items-center gap-2 rounded-t-lg border-t border-x transition-all h-[calc(100%-4px)]
                    ${activeTab === 'logs' 
                        ? 'text-teal-700 bg-white border-slate-200 border-b-transparent shadow-[0_-2px_4px_rgba(0,0,0,0.02)] z-10' 
                        : 'text-slate-500 border-transparent hover:text-slate-700 hover:bg-slate-100'}`}
                >
                   <Terminal size={14}/> Execution Logs
               </button>
               <button 
                  onClick={() => setActiveTab('variables')}
                  className={`px-4 py-2 text-xs font-bold flex items-center gap-2 rounded-t-lg border-t border-x transition-all h-[calc(100%-4px)]
                    ${activeTab === 'variables' 
                        ? 'text-teal-700 bg-white border-slate-200 border-b-transparent shadow-[0_-2px_4px_rgba(0,0,0,0.02)] z-10' 
                        : 'text-slate-500 border-transparent hover:text-slate-700 hover:bg-slate-100'}`}
                >
                   <Database size={14}/> Variables
                   <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${activeTab === 'variables' ? 'bg-teal-100 text-teal-700' : 'bg-slate-200 text-slate-600'}`}>
                       {Object.keys(simulationState.variables).length}
                   </span>
               </button>
           </div>
           <div className="flex items-center gap-2 pr-2">
               <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-200 rounded dark:hover:bg-slate-800 transition-colors">
                   <ChevronDown size={16}/>
               </button>
           </div>
       </div>

       {/* Content */}
       <div className="flex-1 overflow-auto p-0 font-mono text-xs bg-white dark:bg-slate-950 relative">
           {activeTab === 'logs' ? (
               <div className="p-4 space-y-1.5">
                   {simulationState.logs.length === 0 && (
                       <div className="text-slate-400 italic flex items-center gap-2 opacity-50 p-4">
                           <Terminal size={14}/> 
                           Ready to simulate...
                       </div>
                   )}
                   {simulationState.logs.map((log, i) => (
                       <div key={i} className="flex gap-3 hover:bg-slate-50 dark:hover:bg-slate-900 p-1 rounded -mx-1">
                           <span className="text-slate-400 select-none min-w-[70px]">[{new Date().toLocaleTimeString().split(' ')[0]}]</span>
                           <span className="text-slate-700 dark:text-slate-300 break-all">{log}</span>
                       </div>
                   ))}
                   <div ref={logsEndRef} />
               </div>
           ) : (
               <div className="w-full">
                  <table className="w-full text-left border-collapse">
                      <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 sticky top-0 z-10 shadow-sm">
                          <tr>
                              <th className="font-semibold py-2 px-4 border-b border-slate-200 dark:border-slate-800 w-1/3 text-[10px] uppercase tracking-wider">Variable Name</th>
                              <th className="font-semibold py-2 px-4 border-b border-slate-200 dark:border-slate-800 text-[10px] uppercase tracking-wider">Current Value</th>
                              <th className="font-semibold py-2 px-4 border-b border-slate-200 dark:border-slate-800 w-12"></th>
                          </tr>
                      </thead>
                      <tbody>
                        {Object.entries(simulationState.variables).map(([key, value]) => (
                            <tr key={key} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900 group transition-colors">
                                <td className="py-2 px-4 text-teal-700 dark:text-teal-400 font-bold">{key}</td>
                                <td className="py-2 px-4 text-slate-700 dark:text-slate-300">
                                    {typeof value === 'object' ? (
                                        <pre className="text-[10px] bg-slate-100 dark:bg-slate-900 p-1 rounded max-h-20 overflow-auto border border-slate-200">
                                            {JSON.stringify(value, null, 2)}
                                        </pre>
                                    ) : (
                                        String(value)
                                    )}
                                </td>
                                <td className="py-2 px-4 text-right">
                                    {/* Placeholder for delete/edit actions if implemented */}
                                </td>
                            </tr>
                        ))}
                        {Object.keys(simulationState.variables).length === 0 && (
                            <tr>
                                <td colSpan={3} className="py-8 text-center text-slate-400 italic">
                                    No global variables defined.
                                </td>
                            </tr>
                        )}
                      </tbody>
                  </table>
               </div>
           )}
       </div>
    </div>
  );
};

export default BottomPanel;